import React, { useState, useEffect } from "react";
import globals from '../../../Globals';
import { Users, CheckCircle, XCircle, RefreshCw, Search, Filter, Eye, Trash2, Edit } from 'lucide-react';

const API = globals.API;

const DashboardUsers = () => {
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activationModal, setActivationModal] = useState({ show: false, user: null });
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [detailsModal, setDetailsModal] = useState({ show: false, user: null });
  const [updateModal, setUpdateModal] = useState({ show: false, user: null });
const [updateForm, setUpdateForm] = useState({});

  const token = localStorage.getItem('authToken');

  useEffect(() => {
    // Check if token exists
    if (!token) {
      showMessage('error', 'No token found. Please login first.');
      setLoading(false);
      return;
    }
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    
    if (!token) {
      showMessage('error', 'Authentication required. Please login.');
      setLoading(false);
      return;
    }

    try {
      const allResponse = await fetch(`${API}/users/`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (allResponse.status === 401) {
        showMessage('error', 'Session expired. Please login again.');
        setLoading(false);
        return;
      }

      const allData = await allResponse.json();

      const pendingResponse = await fetch(`${API}/users/pending`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (pendingResponse.status === 401) {
        showMessage('error', 'Session expired. Please login again.');
        setLoading(false);
        return;
      }

      const pendingData = await pendingResponse.json();

      setUsers(allData.users || []);
      setPendingUsers(pendingData.users || []);
      setLoading(false);
    } catch (error) {
      showMessage('error', 'Failed to fetch users: ' + error.message);
      setLoading(false);
    }
  };
  const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const handleUpdateUser = async (e) => {
  e.preventDefault();
  
  try {
    const response = await fetch(`${API}/users/${updateModal.user._id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updateForm)
    });

    const data = await response.json();

    if (response.ok) {
      showMessage('success', 'User updated successfully!');
      setUpdateModal({ show: false, user: null });
      setUpdateForm({});
      fetchUsers();
    } else {
      showMessage('error', data.message || 'Update failed');
    }
  } catch (error) {
    showMessage('error', 'Error updating user');
  }
};

  const handleActivateUser = async () => {
    if (!password || password.length < 6) {
      showMessage('error', 'Password must be at least 6 characters');
      return;
    }

    try {
      const response = await fetch(`${API}/users/activate/${activationModal.user._id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ password })
      });

      const data = await response.json();

      if (response.ok) {
        showMessage('success', 'User activated successfully!');
        setActivationModal({ show: false, user: null });
        setPassword('');
        fetchUsers();
      } else {
        showMessage('error', data.message || 'Activation failed');
      }
    } catch (error) {
      showMessage('error', 'Error activating user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const response = await fetch(`${API}/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (response.ok) {
        showMessage('success', 'User deleted successfully');
        fetchUsers();
      } else {
        showMessage('error', data.message || 'Delete failed');
      }
    } catch (error) {
      showMessage('error', 'Error deleting user');
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const getFilteredUsers = () => {
    let filtered = [];
    
    if (filter === 'all') {
      filtered = users;
    } else if (filter === 'active') {
      filtered = users.filter(u => u.isActive);
    } else if (filter === 'pending') {
      filtered = users.filter(u => !u.isActive);
    }

    if (searchTerm) {
      filtered = filtered.filter(u => 
        u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const filteredUsers = getFilteredUsers();

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f9fafb',
      padding: '24px'
    },
    maxWidth: {
      maxWidth: '1280px',
      margin: '0 auto'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      padding: '24px',
      marginBottom: '24px'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '16px'
    },
    titleSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#111827',
      margin: 0
    },
    subtitle: {
      color: '#6b7280',
      fontSize: '14px',
      margin: 0
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      backgroundColor: '#2563eb',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '16px'
    },
    statBox: {
      padding: '16px',
      borderRadius: '8px'
    },
    updateBtn: {
  padding: '8px',
  backgroundColor: 'transparent',
  color: '#3b82f6',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer'
},
formInput: {
  width: '100%',
  padding: '8px 16px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box'
},
    statLabel: {
      fontSize: '14px',
      fontWeight: '500',
      marginBottom: '4px'
    },
    statValue: {
      fontSize: '28px',
      fontWeight: 'bold',
      margin: 0
    },
    alert: {
      padding: '16px',
      borderRadius: '8px',
      marginBottom: '24px'
    },
    filterSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    },
    searchBox: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flex: 1
    },
    input: {
      flex: 1,
      border: 'none',
      outline: 'none',
      fontSize: '14px',
      color: '#374151'
    },
    select: {
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      padding: '8px 16px',
      outline: 'none',
      fontSize: '14px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      padding: '12px 24px',
      textAlign: 'left',
      fontSize: '12px',
      fontWeight: '500',
      color: '#6b7280',
      textTransform: 'uppercase',
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb'
    },
    td: {
      padding: '16px 24px',
      borderBottom: '1px solid #e5e7eb'
    },
    badge: {
      padding: '4px 8px',
      fontSize: '12px',
      fontWeight: '500',
      borderRadius: '9999px',
      display: 'inline-block'
    },
    statusBadge: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '14px'
    },
    actionButtons: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    activateBtn: {
      padding: '6px 12px',
      backgroundColor: '#16a34a',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px'
    },
    deleteBtn: {
      padding: '8px',
      backgroundColor: 'transparent',
      color: '#dc2626',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer'
    },
   modal: {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px',
  zIndex: 50,
  overflowY: 'auto'  // Add this
},
modalContent: {
  backgroundColor: 'white',
  borderRadius: '8px',
  boxShadow: '0 20px 25px rgba(0,0,0,0.15)',
  maxWidth: '600px',  // Change from 448px to 600px for details modal
  width: '100%',
  padding: '24px',
  maxHeight: '90vh',  // Add this
  overflowY: 'auto',  // Add this
  margin: '20px 0'    // Add this for spacing top/bottom
},
    modalTitle: {
      fontSize: '20px',
      fontWeight: 'bold',
      color: '#111827',
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '8px'
    },
    passwordInput: {
      width: '100%',
      padding: '8px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '14px',
      outline: 'none',
      boxSizing: 'border-box'
    },
    modalButtons: {
      display: 'flex',
      gap: '12px'
    },
    confirmBtn: {
      flex: 1,
      padding: '8px 16px',
      backgroundColor: '#16a34a',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500'
    },
    cancelBtn: {
      flex: 1,
      padding: '8px 16px',
      backgroundColor: '#d1d5db',
      color: '#374151',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500'
    },
    loading: {
      padding: '48px',
      textAlign: 'center',
      color: '#6b7280'
    },
    detailSection: {
  marginBottom: '20px',
  paddingBottom: '20px',
  borderBottom: '1px solid #e5e7eb'
},
detailSectionTitle: {
  fontSize: '16px',
  fontWeight: '600',
  color: '#111827',
  marginBottom: '12px'
},
detailGrid: {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '12px'
},
detailItem: {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px'
},
detailLabel: {
  fontSize: '12px',
  fontWeight: '500',
  color: '#6b7280',
  textTransform: 'uppercase'
},
detailValue: {
  fontSize: '14px',
  color: '#111827',
  fontWeight: '500'
}
  };

  return (
    <div style={styles.container}>
      <div style={styles.maxWidth}>
        {/* Header */}
        <div style={styles.card}>
          <div style={styles.header}>
            <div style={styles.titleSection}>
              <Users color="#2563eb" size={32} />
              <div>
                <h1 style={styles.title}>Users Management</h1>
                <p style={styles.subtitle}>Manage user accounts and activations</p>
              </div>
            </div>
            <button onClick={fetchUsers} style={styles.button}>
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>

          {/* Stats */}
          <div style={styles.statsGrid}>
            <div style={{...styles.statBox, backgroundColor: '#dbeafe'}}>
              <p style={{...styles.statLabel, color: '#2563eb', margin: 0}}>Total Users</p>
              <p style={{...styles.statValue, color: '#1e3a8a'}}>{users.length}</p>
            </div>
            <div style={{...styles.statBox, backgroundColor: '#dcfce7'}}>
              <p style={{...styles.statLabel, color: '#16a34a', margin: 0}}>Active Users</p>
              <p style={{...styles.statValue, color: '#14532d'}}>
                {users.filter(u => u.isActive).length}
              </p>
            </div>
            <div style={{...styles.statBox, backgroundColor: '#fef3c7'}}>
              <p style={{...styles.statLabel, color: '#d97706', margin: 0}}>Pending Approval</p>
              <p style={{...styles.statValue, color: '#78350f'}}>{pendingUsers.length}</p>
            </div>
          </div>
        </div>

        {/* Message Alert */}
        {message.text && (
          <div style={{
            ...styles.alert,
            backgroundColor: message.type === 'success' ? '#dcfce7' : '#fee2e2',
            color: message.type === 'success' ? '#166534' : '#991b1b'
          }}>
            {message.text}
          </div>
        )}

        {/* Filters and Search */}
        <div style={styles.card}>
          <div style={styles.filterSection}>
            <div style={styles.searchBox}>
              <Search color="#9ca3af" size={20} />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.input}
              />
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <Filter color="#9ca3af" size={20} />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                style={styles.select}
              >
                <option value="all">All Users</option>
                <option value="active">Active Only</option>
                <option value="pending">Pending Only</option>
              </select>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div style={{...styles.card, padding: 0, overflow: 'hidden'}}>
          {loading ? (
            <div style={styles.loading}>Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div style={styles.loading}>No users found</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Phone</th>
                  <th style={styles.th}>Role</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user._id} style={{backgroundColor: 'white'}}>
                    <td style={styles.td}>
                      <div style={{fontSize: '14px', fontWeight: '500', color: '#111827'}}>
                        {user.name}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div style={{fontSize: '14px', color: '#6b7280'}}>{user.email}</div>
                    </td>
                    <td style={styles.td}>
                      <div style={{fontSize: '14px', color: '#6b7280'}}>
                        {user.phoneNumber || 'N/A'}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        backgroundColor: user.role === 'admin' ? '#f3e8ff' : '#f3f4f6',
                        color: user.role === 'admin' ? '#6b21a8' : '#374151'
                      }}>
                        {user.role}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {user.isActive ? (
                        <span style={{...styles.statusBadge, color: '#16a34a'}}>
                          <CheckCircle size={16} />
                          Active
                        </span>
                      ) : (
                        <span style={{...styles.statusBadge, color: '#d97706'}}>
                          <XCircle size={16} />
                          Pending
                        </span>
                      )}
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionButtons}>
                        {!user.isActive && (
                          <button
                            onClick={() => setActivationModal({ show: true, user })}
                            style={styles.activateBtn}
                          >
                            Activate
                          </button>
                          
                        )}
                        <button
  onClick={() => setDetailsModal({ show: true, user })}
  style={styles.viewBtn}
  title="View details"
>
  <Eye size={16} />
</button>
                        <button
                          onClick={() => handleDeleteUser(user._id)}
                          style={styles.deleteBtn}
                          title="Delete user"
                        >
                          <Trash2 size={16} />
                        </button>
                        <button
  onClick={() => {
    setUpdateModal({ show: true, user });
    setUpdateForm({ ...user, password: '' });
  }}
  style={styles.updateBtn}
  title="Edit user"
>
  <Edit size={16} />
</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Activation Modal */}
      {activationModal.show && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h3 style={styles.modalTitle}>Activate User Account</h3>
            <div style={{marginBottom: '16px'}}>
              <p style={{fontSize: '14px', color: '#6b7280', marginBottom: '8px'}}>
                User: <span style={{fontWeight: '500'}}>{activationModal.user?.name}</span>
              </p>
              <p style={{fontSize: '14px', color: '#6b7280', marginBottom: '16px'}}>
                Email: <span style={{fontWeight: '500'}}>{activationModal.user?.email}</span>
              </p>
              <label style={styles.label}>Set Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password (min 6 characters)"
                style={styles.passwordInput}
              />
            </div>
            <div style={styles.modalButtons}>
              <button onClick={handleActivateUser} style={styles.confirmBtn}>
                Activate
              </button>
              <button
                onClick={() => {
                  setActivationModal({ show: false, user: null });
                  setPassword('');
                }}
                style={styles.cancelBtn}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Modal */}
{updateModal.show && updateModal.user && (
  <div style={styles.modal}>
    <div style={styles.modalContent}>
      <h3 style={styles.modalTitle}>Update User</h3>
      <form onSubmit={handleUpdateUser}>
        <div style={{marginBottom: '16px'}}>
          <label style={styles.label}>Name</label>
          <input
            type="text"
            value={updateForm.name || ''}
            onChange={(e) => setUpdateForm({ ...updateForm, name: e.target.value })}
            style={styles.formInput}
          />
        </div>
        
        <div style={{marginBottom: '16px'}}>
          <label style={styles.label}>Email</label>
          <input
            type="email"
            value={updateForm.email || ''}
            onChange={(e) => setUpdateForm({ ...updateForm, email: e.target.value })}
            style={styles.formInput}
          />
        </div>
        
        <div style={{marginBottom: '16px'}}>
          <label style={styles.label}>Phone Number</label>
          <input
            type="text"
            value={updateForm.phoneNumber || ''}
            onChange={(e) => setUpdateForm({ ...updateForm, phoneNumber: e.target.value })}
            style={styles.formInput}
          />
        </div>
        
        <div style={{marginBottom: '16px'}}>
          <label style={styles.label}>Password (leave empty to keep current)</label>
          <input
            type="password"
            value={updateForm.password || ''}
            onChange={(e) => setUpdateForm({ ...updateForm, password: e.target.value })}
            style={styles.formInput}
            placeholder="Enter new password or leave empty"
          />
        </div>

        <div style={styles.modalButtons}>
          <button type="submit" style={styles.confirmBtn}>
            Update User
          </button>
          <button
            type="button"
            onClick={() => {
              setUpdateModal({ show: false, user: null });
              setUpdateForm({});
            }}
            style={styles.cancelBtn}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  </div>
)}


      {/* User Details Modal */}
{detailsModal.show && detailsModal.user && (
  <div style={styles.modal}>
    <div style={styles.modalContent}>
      <h3 style={styles.modalTitle}>User Details</h3>
      
      {/* Basic Information */}
      <div style={styles.detailSection}>
        <h4 style={styles.detailSectionTitle}>Basic Information</h4>
        <div style={styles.detailGrid}>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Name</span>
            <span style={styles.detailValue}>{detailsModal.user.name}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Email</span>
            <span style={styles.detailValue}>{detailsModal.user.email}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Phone Number</span>
            <span style={styles.detailValue}>{detailsModal.user.phoneNumber || 'N/A'}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Role</span>
            <span style={styles.detailValue}>{detailsModal.user.role}</span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Status</span>
            <span style={{...styles.detailValue, color: detailsModal.user.isActive ? '#16a34a' : '#d97706'}}>
              {detailsModal.user.isActive ? 'Active' : 'Pending'}
            </span>
          </div>
          <div style={styles.detailItem}>
            <span style={styles.detailLabel}>Created At</span>
            <span style={styles.detailValue}>{formatDate(detailsModal.user.createdAt)}</span>
          </div>
        </div>
      </div>

      {/* Specifications */}
      {detailsModal.user.specifications && Object.keys(detailsModal.user.specifications).length > 0 && (
        <div style={styles.detailSection}>
          <h4 style={styles.detailSectionTitle}>Specifications</h4>
          <div style={styles.detailGrid}>
            {Object.entries(detailsModal.user.specifications).map(([key, value]) => (
              <div key={key} style={styles.detailItem}>
                <span style={styles.detailLabel}>{key}</span>
                <span style={styles.detailValue}>{value || 'N/A'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Company Information (for role "other") */}
      {detailsModal.user.role === 'other' && (
        <>
          <div style={styles.detailSection}>
            <h4 style={styles.detailSectionTitle}>Company Information</h4>
            <div style={styles.detailGrid}>
              {detailsModal.user.raisonSociale && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Raison Sociale</span>
                  <span style={styles.detailValue}>{detailsModal.user.raisonSociale}</span>
                </div>
              )}
              {detailsModal.user.capitalSocial && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Capital Social</span>
                  <span style={styles.detailValue}>{detailsModal.user.capitalSocial}</span>
                </div>
              )}
              {detailsModal.user.adresse && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Adresse</span>
                  <span style={styles.detailValue}>{detailsModal.user.adresse}</span>
                </div>
              )}
              {detailsModal.user.ville && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Ville</span>
                  <span style={styles.detailValue}>{detailsModal.user.ville}</span>
                </div>
              )}
              {detailsModal.user.telephone && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Téléphone</span>
                  <span style={styles.detailValue}>{detailsModal.user.telephone}</span>
                </div>
              )}
              {detailsModal.user.emailContact && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Email Contact</span>
                  <span style={styles.detailValue}>{detailsModal.user.emailContact}</span>
                </div>
              )}
              {detailsModal.user.dateCreation && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Date Création</span>
                  <span style={styles.detailValue}>{formatDate(detailsModal.user.dateCreation)}</span>
                </div>
              )}
              {detailsModal.user.numeroIATA && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Numéro IATA</span>
                  <span style={styles.detailValue}>{detailsModal.user.numeroIATA}</span>
                </div>
              )}
              {detailsModal.user.rc && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>RC</span>
                  <span style={styles.detailValue}>{detailsModal.user.rc}</span>
                </div>
              )}
              {detailsModal.user.patente && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Patente</span>
                  <span style={styles.detailValue}>{detailsModal.user.patente}</span>
                </div>
              )}
            </div>
          </div>

          <div style={styles.detailSection}>
            <h4 style={styles.detailSectionTitle}>Signatory Information</h4>
            <div style={styles.detailGrid}>
              {detailsModal.user.personneSignataire && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Personne Signataire</span>
                  <span style={styles.detailValue}>{detailsModal.user.personneSignataire}</span>
                </div>
              )}
              {detailsModal.user.fonctionSignataire && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Fonction Signataire</span>
                  <span style={styles.detailValue}>{detailsModal.user.fonctionSignataire}</span>
                </div>
              )}
            </div>
          </div>

          <div style={styles.detailSection}>
            <h4 style={styles.detailSectionTitle}>Contact Persons</h4>
            <div style={styles.detailGrid}>
              {detailsModal.user.nomPremierResponsable && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Premier Responsable</span>
                  <span style={styles.detailValue}>{detailsModal.user.nomPremierResponsable}</span>
                </div>
              )}
              {detailsModal.user.fonctionPremierResponsable && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Fonction</span>
                  <span style={styles.detailValue}>{detailsModal.user.fonctionPremierResponsable}</span>
                </div>
              )}
              {detailsModal.user.mobilePremierResponsable && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Mobile</span>
                  <span style={styles.detailValue}>{detailsModal.user.mobilePremierResponsable}</span>
                </div>
              )}
              {detailsModal.user.personneContact && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Personne Contact</span>
                  <span style={styles.detailValue}>{detailsModal.user.personneContact}</span>
                </div>
              )}
              {detailsModal.user.fonctionContact && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Fonction Contact</span>
                  <span style={styles.detailValue}>{detailsModal.user.fonctionContact}</span>
                </div>
              )}
              {detailsModal.user.mobileContact && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Mobile Contact</span>
                  <span style={styles.detailValue}>{detailsModal.user.mobileContact}</span>
                </div>
              )}
            </div>
          </div>

          <div style={{...styles.detailSection, borderBottom: 'none'}}>
            <h4 style={styles.detailSectionTitle}>Financial Contact</h4>
            <div style={styles.detailGrid}>
              {detailsModal.user.finance && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Finance</span>
                  <span style={styles.detailValue}>{detailsModal.user.finance}</span>
                </div>
              )}
              {detailsModal.user.mobileFinance && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Mobile Finance</span>
                  <span style={styles.detailValue}>{detailsModal.user.mobileFinance}</span>
                </div>
              )}
              {detailsModal.user.emailFinance && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Email Finance</span>
                  <span style={styles.detailValue}>{detailsModal.user.emailFinance}</span>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <div style={styles.modalButtons}>
        <button
          onClick={() => setDetailsModal({ show: false, user: null })}
          style={{...styles.cancelBtn, flex: 'none', width: '100%'}}
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}
      
    </div>
  );
};









export default DashboardUsers;